import './styles.scss';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Box} from '@mui/material';
import Lottie from 'lottie-react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useLoading} from '@/app/zustand/useLoading';
import {useGroupsRepository} from '@/dataSource/src/api/groups/useGroupsRepository';
import ModakLoaderAnimation from '@/lotties/ModakLoader.json';

import {GROUPS_QUERY_PARAMS} from '../../models/constants';
import {IGroupMember, IGroupParticipant} from '../../models/interfaces';
import {GroupEmptyList} from '../GroupEmptyList/GroupEmptyList';

import {mapExpensesToActivityGroups} from './mapExpensesToActivityGroups';
import {mergeActivityGroups} from './mergeActivityGroups';
import {ActivityGroup, TransactionList} from './TransactionList';

const LIMIT = 10;

export interface GroupDashboardActivitiesProps {
  members: IGroupMember[];
  groupCurrentParticipant: IGroupParticipant;
  ownerGroupParticipant: IGroupParticipant;
}

const GroupDashboardActivities: React.FC<GroupDashboardActivitiesProps> = ({
  groupCurrentParticipant,
  ownerGroupParticipant,
  members,
}) => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const [groups, setGroups] = useState<ActivityGroup[]>([]);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const {setSkipLoading} = useLoading();

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isInitialFetch = useRef(true);

  const {handleGetGroupExpenses, isLoadingGroupExpenses} =
    useGroupsRepository();

  const groupId = searchParams.get(GROUPS_QUERY_PARAMS.GROUP_ID);

  const hasActivity = useMemo(() => groups && groups.length > 0, [groups]);

  // =========================
  // FETCH
  // =========================

  const fetchExpenses = useCallback(
    async (cursor?: string) => {
      if (!groupId) return;

      try {
        setSkipLoading(true);
        const response = await handleGetGroupExpenses(groupId, LIMIT, cursor);

        const newGroups = mapExpensesToActivityGroups(
          response.items,
          groupCurrentParticipant,
          ownerGroupParticipant,
          members,
          t,
        );

        setGroups(prev => {
          if (!cursor) return newGroups; // first page — replace
          return mergeActivityGroups(prev, newGroups); // next pages — merge by date title
        });

        setLastKey(response.last_key);
        setHasMore(!!response.last_key);
      } finally {
        setIsFetchingMore(false);
      }
    },
    [
      groupId,
      setSkipLoading,
      handleGetGroupExpenses,
      groupCurrentParticipant,
      ownerGroupParticipant,
      members,
      t,
    ],
  );

  // =========================
  // INITIAL FETCH
  // =========================

  useEffect(() => {
    if (!isInitialFetch.current) return;
    isInitialFetch.current = false;
    fetchExpenses();
  }, [fetchExpenses]);

  // =========================
  // INFINITE SCROLL
  // =========================

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasMore &&
          !isFetchingMore &&
          !isLoadingGroupExpenses
        ) {
          setIsFetchingMore(true);
          fetchExpenses(lastKey ?? undefined);
        }
      },
      {threshold: 0.1},
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchExpenses, hasMore, isFetchingMore, isLoadingGroupExpenses, lastKey]);

  const modakLoader = useMemo(
    () => (
      <Lottie
        animationData={ModakLoaderAnimation}
        loop
        style={{
          width: 48,
          height: 48,
        }}
      />
    ),
    [],
  );

  return (
    <div className="group-dashboard-activities">
      {isLoadingGroupExpenses && groups.length === 0 ? (
        <Box className="group-dashboard-activities__loader">{modakLoader}</Box>
      ) : hasActivity ? (
        <>
          {groups.map(group => (
            <TransactionList
              key={group.title}
              group={group}
            />
          ))}

          {/* Sentinel: IntersectionObserver watches this to trigger next page */}
          <div
            ref={sentinelRef}
            style={{height: 1}}
          />

          {isFetchingMore && (
            <Box className="group-dashboard-activities__loader">
              {modakLoader}
            </Box>
          )}
        </>
      ) : (
        <Box className="groups-dashboard-balances">
          <GroupEmptyList text={t('groups.dashboard.activities.empty')} />
        </Box>
      )}
    </div>
  );
};

export default GroupDashboardActivities;
